import React from 'react';
import { Box } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import { GuVYear, CashflowYear } from '../hooks/useCalculationPageData';
import { colors, fonts } from '../../../../styles/theme';
import Section from '../../../layout/Section';

interface EconomySectionProps {
    guvData: GuVYear[];
    cashflowData: CashflowYear[];
}

// Feste Farben für die Linien (P-Value Farbschema)
const COLORS = {
    ergebnisNachSteuern: colors.chart.p50, // Pastellgrün
    cashflow: colors.chart.p90, // Pastellblau
};

/**
 * Formatiert Zahlen im deutschen Format mit Tausendertrennzeichen
 */
const formatCurrency = (value: number): string => {
    return value.toLocaleString('de-DE', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }) + ' €';
};

/**
 * 
 * Zeigt ein Liniendiagramm mit:
 * - Ergebnis nach Steuern (aus GuV)
 * - Cashflow nach Schuldendienst (aus Cashflow)
 * 
 * Moderne, minimale Darstellung ohne Überschrift
 */
const EconomySection: React.FC<EconomySectionProps> = ({ guvData, cashflowData }) => {
    // Keine Daten vorhanden
    if ((!guvData || guvData.length === 0) && (!cashflowData || cashflowData.length === 0)) {
        return null;
    }

    // Daten für nivo/line vorbereiten
    const chartData = [
        {
            id: 'Ergebnis n. St.',
            color: COLORS.ergebnisNachSteuern,
            data: guvData.map(item => ({
                x: item.jahr,
                y: item.ergebnisNachSteuern,
            })),
        },
        {
            id: 'Cashflow',
            color: COLORS.cashflow,
            data: cashflowData.map(item => ({
                x: item.jahr,
                y: item.cashflowNachSchuldendienst,
            })),
        },
    ].filter(series => series.data.length > 0);

    // Min/Max für Y-Achse berechnen (mit Puffer)
    const allValues = [
        ...guvData.map(d => d.ergebnisNachSteuern),
        ...cashflowData.map(d => d.cashflowNachSchuldendienst),
    ];
    const minValue = Math.min(...allValues, 0);
    const maxValue = Math.max(...allValues, 0);
    const padding = (maxValue - minValue) * 0.1;

    return (
        <Section noInnerPadding>
            <Box
                sx={{
                    height: '300px',
                    p: 2,
                    pt: 3,
                    position: 'relative',
                    zIndex: 1300,
                }}
            >
                <ResponsiveLine
                    data={chartData}
                    margin={{ top: 50, right: 30, bottom: 50, left: 80 }}
                    xScale={{ type: 'point' }}
                    yScale={{
                        type: 'linear',
                        min: minValue - padding,
                        max: maxValue + padding,
                        stacked: false,
                    }}
                    curve="monotoneX"
                    colors={d => d.color}
                    lineWidth={3}
                    enablePoints={true}
                    pointSize={5}
                    pointColor={{ from: 'color' }}
                    pointBorderWidth={0}
                    enableArea={true}
                    areaOpacity={0.12}
                    areaBlendMode="normal"
                    enableGridX={false}
                    enableGridY={false}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                        tickSize: 0,
                        tickPadding: 12,
                        tickRotation: 0,
                    }}
                    axisLeft={{
                        tickSize: 0,
                        tickPadding: 12,
                        tickRotation: 0,
                        format: (value) => {
                            // Kompakte Darstellung für große Zahlen
                            if (Math.abs(value) >= 1000000) {
                                return `${(value / 1000000).toFixed(1)} Mio`;
                            }
                            if (Math.abs(value) >= 1000) {
                                return `${(value / 1000).toFixed(0)} T`;
                            }
                            return value.toString();
                        },
                        tickValues: 5,
                    }}
                    theme={{
                        axis: {
                            domain: {
                                line: {
                                    stroke: 'transparent',
                                },
                            },
                            ticks: {
                                text: {
                                    fill: colors.chart.text,
                                    fontSize: 14,
                                    fontFamily: fonts.primary,
                                    fontWeight: 500,
                                },
                            },
                            legend: {
                                text: {
                                    fill: colors.chart.text,
                                    fontSize: 14,
                                    fontFamily: fonts.primary,
                                    fontWeight: 500,
                                },
                            },
                        },
                        crosshair: {
                            line: {
                                stroke: colors.chart.text,
                                strokeWidth: 1,
                                strokeOpacity: 0.5,
                            },
                        },
                        legends: {
                            text: {
                                fontSize: 14,
                                fontWeight: 500,
                                fontFamily: fonts.primary,
                                fill: colors.chart.text,
                            },
                        },
                    }}
                    // Nulllinie als Marker
                    markers={[
                        {
                            axis: 'y',
                            value: 0,
                            lineStyle: {
                                stroke: colors.border.medium,
                                strokeWidth: 1,
                                strokeDasharray: '4 4',
                            },
                        },
                    ]}
                    // Legende als Überschrift über dem Chart
                    legends={[
                        {
                            anchor: 'top',
                            direction: 'row',
                            justify: false,
                            translateX: 0,
                            translateY: -40,
                            itemsSpacing: 50,
                            itemDirection: 'left-to-right',
                            itemWidth: 180,
                            itemHeight: 24,
                            itemOpacity: 1,
                            symbolSize: 12,
                            symbolShape: 'circle',
                            itemTextColor: colors.text.primary,
                            effects: [
                                {
                                    on: 'hover',
                                    style: {
                                        itemTextColor: colors.text.primary,
                                    },
                                },
                            ],
                        },
                    ]}
                    useMesh={true}
                    enableSlices="x"
                    sliceTooltip={({ slice }) => (
                        <Box
                            sx={{
                                background: colors.background.paper,
                                padding: '12px 16px',
                                minWidth: 230,
                                border: `1px solid ${colors.border.light}`,
                                borderRadius: '8px',
                                boxShadow: `0 4px 12px ${colors.shadow.medium}`,
                                zIndex: 1300,
                            }}
                        >
                            <Box
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    color: colors.text.primary,
                                    mb: 1,
                                    pb: 1,
                                    borderBottom: `1px solid ${colors.border.light}`,
                                }}
                            >
                                Jahr {slice.points[0].data.x}
                            </Box>
                            {slice.points.map(point => (
                                <Box
                                    key={point.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        py: 0.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            backgroundColor: point.seriesColor,
                                        }}
                                    />
                                    <Box sx={{ fontSize: '0.75rem', color: colors.text.secondary }}>
                                        {point.seriesId}:
                                    </Box>
                                    <Box
                                        sx={{
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            color: point.seriesColor,
                                            fontFamily: '"JetBrains Mono", monospace',
                                        }}
                                    >
                                        {formatCurrency(point.data.y as number)}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}
                    animate={true}
                    motionConfig="gentle"
                />
            </Box>
        </Section>
    );
};

export default React.memo(EconomySection);

